import store from './store';
import { setOnlineStatus, setOutboxCount } from './store/slices/offlineSlice';
import { db } from './indexedDB';

const SW_URL = `${process.env.PUBLIC_URL}/service-worker.js`;

// #region New version handling
// After a deploy, an open page keeps running the old code (and the service worker keeps serving
// the old files) until the page reloads. The new service worker installs in the background and
// waits; the app tells the user ("New version available - Reload") and only switches when they
// click, so nobody loses half-filled forms. UpdateAvailableNotice shows the message.
const UPDATE_EVENT = 'app-update-available';
// Tabs that stay open for hours look for a new deploy this often (otherwise only on page load).
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

let waitingWorker = null;    // the new version's service worker, waiting to take over
let updateAvailable = false;
let reloadRequested = false; // this tab asked for the switch (Reload clicked)

function notifyUpdateAvailable(worker) {
    if (worker) waitingWorker = worker;
    updateAvailable = true;
    window.dispatchEvent(new Event(UPDATE_EVENT));
}

export const isUpdateAvailable = () => updateAvailable;

// Subscribe to "a new version is available"; returns the unsubscribe function.
export function onUpdateAvailable(callback) {
    window.addEventListener(UPDATE_EVENT, callback);
    return () => window.removeEventListener(UPDATE_EVENT, callback);
}

// Switches to the new version: the waiting service worker takes over, then this page reloads
// (see the controllerchange listener). If another tab already switched, just reload.
export function applyUpdate() {
    reloadRequested = true;
    if (waitingWorker) {
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else {
        window.location.reload();
    }
}
// #endregion

function updateBadge(count) {
    if (!('setAppBadge' in navigator)) return;
    if (count > 0) {
        navigator.setAppBadge(count);
    } else {
        navigator.clearAppBadge();
    }
}

async function getPendingCount() {
    const [outbox, actionQueue] = await Promise.all([
        db.outbox.count(),
        db.action_queue.count(),
    ]);
    return outbox + actionQueue;
}

export function register() {
    if (!('serviceWorker' in navigator)) return;

    // Unregister any stale SW that isn't our current one (e.g. old serviceworker.js)
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const reg of registrations) {
            const swUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
            if (swUrl && !swUrl.endsWith('service-worker.js')) {
                reg.unregister();
                console.log('🧹 Unregistered stale SW:', swUrl);
            }
        }
    });

    // No controller yet = first visit: the first install isn't an "update".
    let hadController = Boolean(navigator.serviceWorker.controller);

    // The active service worker changed. Either this tab clicked Reload (switch now), or another
    // tab switched: this page still runs the old code, so offer the reload here too.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController) {
            hadController = true;
            return;
        }
        if (reloadRequested) {
            window.location.reload();
            return;
        }
        notifyUpdateAvailable(null);
    });

    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register(SW_URL)
            .then((registration) => {
                console.log('✅ Service worker registered:', registration.scope);

                // A new version installed during an earlier visit and still waiting.
                if (registration.waiting && navigator.serviceWorker.controller) {
                    notifyUpdateAvailable(registration.waiting);
                }

                registration.onupdatefound = () => {
                    const installing = registration.installing;
                    if (!installing) return;

                    installing.onstatechange = () => {
                        // Installed while an older version controls the page = a new deploy.
                        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                            notifyUpdateAvailable(installing);
                        }
                    };
                };

                setInterval(() => {
                    registration.update().catch(() => {
                        // Offline or server unreachable: try again next time.
                    });
                }, UPDATE_CHECK_INTERVAL_MS);
            })
            .catch((err) => console.error('❌ Service worker registration failed:', err));

        // Listen for outbox updates posted by the SW
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'OUTBOX_UPDATED') {
                getPendingCount().then((count) => {
                    store.dispatch(setOutboxCount(count));
                    updateBadge(count);
                });
            }
        });
    });

    // Sync count and badge on startup
    getPendingCount().then((count) => {
        store.dispatch(setOutboxCount(count));
        updateBadge(count);
    });

    window.addEventListener('online', () => store.dispatch(setOnlineStatus(true)));
    window.addEventListener('offline', () => store.dispatch(setOnlineStatus(false)));
}

export function unregister() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => registration.unregister())
            .catch((err) => console.error('❌ Service worker unregister failed:', err));
    }
}
